// =============================================================================
// E-Signature Types
// =============================================================================

// Enums matching backend Prisma schema
export type SignatureFieldType =
  | 'SIGNATURE' | 'INITIALS' | 'DATE_SIGNED' | 'TEXT' | 'CHECKBOX'
  | 'NAME' | 'EMAIL_FIELD' | 'COMPANY' | 'TITLE_FIELD';

export type EnvelopeStatus =
  | 'ENVELOPE_DRAFT' | 'ENVELOPE_SENT' | 'ENVELOPE_PARTIALLY_SIGNED'
  | 'ENVELOPE_COMPLETED' | 'ENVELOPE_DECLINED' | 'ENVELOPE_VOIDED' | 'ENVELOPE_EXPIRED';

export type SignerStatus =
  | 'SIGNER_PENDING' | 'SIGNER_SENT' | 'SIGNER_DELIVERED'
  | 'SIGNER_VIEWED' | 'SIGNER_SIGNED' | 'SIGNER_DECLINED';

export type SignatureDocType =
  | 'NDA' | 'SUBSCRIPTION' | 'SIDE_LETTER' | 'PPM_ACKNOWLEDGMENT' | 'TAX_FORM' | 'ACCREDITATION' | 'OTHER';

export type SignatureMethod = 'DRAW' | 'TYPE' | 'UPLOAD';

// =============================================================================
// Template Types
// =============================================================================

export interface SignatureTemplate {
  id: string;
  name: string;
  description: string | null;
  documentType: SignatureDocType;
  sourceDocumentPath: string;
  sourceDocumentHash: string;
  sourceDocumentName: string;
  sourceDocumentSize: number;
  pageCount: number;
  isActive: boolean;
  version: number;
  createdById: string;
  createdBy?: { id: string; email: string; firstName: string | null; lastName: string | null };
  fields?: SignatureTemplateField[];
  _count?: { fields: number; envelopes: number };
  createdAt: string;
  updatedAt: string;
}

export interface SignatureTemplateField {
  id: string;
  templateId: string;
  fieldType: SignatureFieldType;
  label: string;
  placeholder: string | null;
  isRequired: boolean;
  pageNumber: number;
  xPercent: number;
  yPercent: number;
  widthPercent: number;
  heightPercent: number;
  signerIndex: number;
  tabOrder: number;
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// Envelope Types
// =============================================================================

export interface SignatureEnvelope {
  id: string;
  templateId: string | null;
  documentPath: string;
  documentHash: string;
  documentName: string;
  documentSize: number;
  pageCount: number;
  status: EnvelopeStatus;
  subject: string;
  message: string | null;
  signingMode: string;
  referenceType: string | null;
  referenceId: string | null;
  sentAt: string | null;
  completedAt: string | null;
  voidedAt: string | null;
  voidedReason: string | null;
  expiresAt: string | null;
  reminderEnabled: boolean;
  reminderFrequencyDays: number;
  remindersSentCount: number;
  signedDocumentPath: string | null;
  certificatePath: string | null;
  createdById: string;
  createdBy?: { id: string; email: string; firstName: string | null; lastName: string | null };
  template?: { id: string; name: string } | null;
  signers: SignatureEnvelopeSigner[];
  createdAt: string;
  updatedAt: string;
}

export interface SignatureEnvelopeSigner {
  id: string;
  envelopeId: string;
  signerIndex: number;
  name: string;
  email: string;
  status: SignerStatus;
  sentAt: string | null;
  viewedAt: string | null;
  signedAt: string | null;
  declinedAt: string | null;
  declinedReason: string | null;
  consentGivenAt: string | null;
  signingIpAddress: string | null;
  signatureMethod: SignatureMethod | null;
  allPagesViewed: boolean;
}

// =============================================================================
// Audit Event Types
// =============================================================================

export interface SignatureAuditEvent {
  id: string;
  envelopeId: string;
  eventType: string;
  actorType: string;
  actorId: string | null;
  actorName: string | null;
  actorEmail: string | null;
  description: string;
  details: Record<string, unknown> | null;
  ipAddress: string | null;
  eventHash: string;
  previousHash: string | null;
  sequenceNumber: number;
  createdAt: string;
}

// =============================================================================
// Signing Session Types (Public)
// =============================================================================

export interface SigningSessionData {
  signer: {
    id: string;
    name: string;
    email: string;
  };
  envelope: {
    id: string;
    subject: string;
    message: string | null;
    documentName: string;
    pageCount: number;
  };
  fields: SigningField[];
  consent: {
    required: boolean;
    alreadyGiven: boolean;
    text: string;
    version: string;
  };
  progress: SigningProgress;
}

export interface SigningField {
  id: string;
  fieldType: SignatureFieldType;
  label: string;
  placeholder: string | null;
  isRequired: boolean;
  pageNumber: number;
  xPercent: number;
  yPercent: number;
  widthPercent: number;
  heightPercent: number;
  tabOrder: number;
  value: string | null;
  isCompleted: boolean;
}

export interface SigningProgress {
  pagesViewed: number;
  totalPages: number;
  fieldsCompleted: number;
  totalRequiredFields: number;
  canComplete: boolean;
}

// =============================================================================
// API Input Types
// =============================================================================

export interface CreateTemplateInput {
  name: string;
  description?: string;
  documentType: SignatureDocType;
}

export interface CreateTemplateFieldInput {
  fieldType: SignatureFieldType;
  label: string;
  placeholder?: string;
  isRequired?: boolean;
  pageNumber: number;
  xPercent: number;
  yPercent: number;
  widthPercent: number;
  heightPercent: number;
  signerIndex?: number;
  tabOrder?: number;
}

export interface CreateEnvelopeInput {
  templateId?: string;
  subject: string;
  message?: string;
  signers: Array<{
    name: string;
    email: string;
    signerIndex: number;
    contactId?: string;
    userId?: string;
  }>;
  signingMode?: 'SEQUENTIAL' | 'PARALLEL';
  expiresInDays?: number;
  reminderEnabled?: boolean;
  reminderFrequencyDays?: number;
  referenceType?: string;
  referenceId?: string;
}

// =============================================================================
// Helper: Status display
// =============================================================================

export const ENVELOPE_STATUS_LABELS: Record<EnvelopeStatus, string> = {
  ENVELOPE_DRAFT: 'Draft',
  ENVELOPE_SENT: 'Sent',
  ENVELOPE_PARTIALLY_SIGNED: 'Partially Signed',
  ENVELOPE_COMPLETED: 'Completed',
  ENVELOPE_DECLINED: 'Declined',
  ENVELOPE_VOIDED: 'Voided',
  ENVELOPE_EXPIRED: 'Expired',
};

export const ENVELOPE_STATUS_COLORS: Record<EnvelopeStatus, string> = {
  ENVELOPE_DRAFT: 'gray',
  ENVELOPE_SENT: 'blue',
  ENVELOPE_PARTIALLY_SIGNED: 'yellow',
  ENVELOPE_COMPLETED: 'green',
  ENVELOPE_DECLINED: 'red',
  ENVELOPE_VOIDED: 'red',
  ENVELOPE_EXPIRED: 'orange',
};

export const SIGNER_STATUS_LABELS: Record<SignerStatus, string> = {
  SIGNER_PENDING: 'Pending',
  SIGNER_SENT: 'Sent',
  SIGNER_DELIVERED: 'Delivered',
  SIGNER_VIEWED: 'Viewed',
  SIGNER_SIGNED: 'Signed',
  SIGNER_DECLINED: 'Declined',
};
